import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import { useBreakpoints } from "core/hooks/useBreakpoints";
import { useCanManage } from "core/hooks/useCanManage";
import { ImpersonationHeaderIcon } from "features/Impersonation/components/ImpersonationHeaderIcon";
import { MyLinksMenu } from "features/MyLinks/components/MyLinksMenu";
import { CommunityFeedback } from "features/Notifications/CommunityFeedback";
import { HeaderNotifications } from "features/Notifications/HeaderNotifications";
import { PitstopAlertBanner } from "features/PitstopAlerts/components/PitstopAlertBanner";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrandLogo } from "./BrandLogo";
import { CustomLink } from "./CustomLink";
import { GlobalAlertsBar, useGlobalAlertsBar } from "./GlobalAlertsBar";
import { MobileNav } from "./MobileNav";
import { DesktopGlobalHeaderNav } from "./Nav";
import SearchBar from "./SearchBar";
import type { MenuOption } from "./UserMenu/shared";
import { UserMenu } from "./UserMenu/UserMenu";

const menuOptions: MenuOption[] = ["user-preferences", "logout"];
export const GLOBAL_HEADER_CSS_VAR_HEIGHT = "--global-header-height";

const globalHeaderHeightRef = (el: HTMLDivElement) => {
  if (!el) {
    return;
  }
  const observer = new ResizeObserver((entries) => {
    if (typeof window === "undefined") {
      return;
    }
    const entry = entries.at(0);
    if (!entry) {
      return;
    }
    const root = window.document.documentElement;
    root.style.setProperty(
      GLOBAL_HEADER_CSS_VAR_HEIGHT,
      `${entry.contentRect.height}px`
    );
  });
  observer.observe(el);
  return () => {
    observer.disconnect();
  };
};

export function GlobalHeader() {
  const [isMobileNavMenuOpen, setIsMobileNavMenuOpen] = useState(false);
  const { events } = useRouter();
  const canManage = useCanManage();
  const { isDesktop } = useBreakpoints();
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:GlobalHeader",
  });
  const closeMenu = useCallback(() => {
    setIsMobileNavMenuOpen(false);
  }, []);
  const { alerts, onAlertClose } = useGlobalAlertsBar();
  useEffect(() => {
    const eventArgs: Parameters<typeof events.on> = [
      "routeChangeStart",
      closeMenu,
    ];
    events.on(...eventArgs);
    return () => {
      events.off(...eventArgs);
    };
  }, [events, closeMenu]);
  if (isDesktop) {
    return (
      <>
        <Box
          ref={globalHeaderHeightRef}
          component="header"
          sx={{
            top: 0,
            position: "sticky",
            boxShadow: (theme) => theme.shadows[1],
            zIndex: 500,
          }}
        >
          <AppBar position="static" component="section">
            <Toolbar
              disableGutters
              sx={{ px: 3, justifyContent: "space-between", gap: 3 }}
            >
              <SearchBar />
              {canManage && (
                <Button
                  LinkComponent={CustomLink}
                  href="/manage"
                  sx={{ color: "primary.contrastText" }}
                >
                  {t("manage")}
                </Button>
              )}

              <MyLinksMenu />
              <HeaderNotifications />
              <CommunityFeedback />
              <ImpersonationHeaderIcon />
              <UserMenu menuItems={menuOptions} />
            </Toolbar>
          </AppBar>
          <DesktopGlobalHeaderNav />
        </Box>
        <GlobalAlertsBar alerts={alerts} onClose={onAlertClose} />
        <PitstopAlertBanner
          containerProps={{ sx: { mx: "auto", mt: 2 }, maxWidth: "xl" }}
        />
      </>
    );
  }
  // Mobile header
  return (
    <>
      <Box
        ref={globalHeaderHeightRef}
        data-testid="mobile-header"
        component="header"
        sx={{
          top: 0,
          position: "sticky",
          boxShadow: (theme) => theme.shadows[1],
          zIndex: 500,
        }}
      >
        <AppBar position="static" component="section">
          <Toolbar
            disableGutters
            sx={{
              display: "grid",
              justifyItems: "center",
              gridTemplateColumns: "105px 1fr 105px",
              gap: 3,
            }}
          >
            <Stack direction="row">
              <IconButton
                data-testid="open-mobile-menu-button"
                aria-label={t("menuButtonAriaLabel")}
                onClick={() => {
                  setIsMobileNavMenuOpen(true);
                }}
              >
                <MenuIcon sx={{ color: "primary.contrastText" }} />
              </IconButton>
            </Stack>
            <Box
              py={1}
              maxWidth={175}
              display="grid"
              sx={{ placeItems: "center" }}
            >
              <CustomLink href="/" display="flex">
                <BrandLogo />
              </CustomLink>
            </Box>
            <Stack direction="row" justifyContent="end">
              <HeaderNotifications />
              <ImpersonationHeaderIcon />
              <UserMenu menuItems={menuOptions} />
            </Stack>
          </Toolbar>
        </AppBar>
      </Box>
      <MobileNav open={isMobileNavMenuOpen} handleClose={closeMenu} />
      <GlobalAlertsBar alerts={alerts} onClose={onAlertClose} />
      <PitstopAlertBanner
        containerProps={{ sx: { mx: "auto", mt: 1 }, maxWidth: "xl" }}
      />
    </>
  );
}
